/**
 * Relative-time formatting for review rows ("3 days ago"). Uses the platform
 * `Intl.RelativeTimeFormat` rather than pulling in a date library — review
 * rows are the only surface that needs this today.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

const THRESHOLDS: { limit: number; unit: Intl.RelativeTimeFormatUnit; ms: number }[] = [
  { limit: HOUR, unit: 'minute', ms: MINUTE },
  { limit: DAY, unit: 'hour', ms: HOUR },
  { limit: WEEK, unit: 'day', ms: DAY },
  { limit: MONTH, unit: 'week', ms: WEEK },
  { limit: YEAR, unit: 'month', ms: MONTH },
];

/**
 * Format a past timestamp as a relative string. Anything under a minute reads
 * "just now"; a future timestamp (clock skew) is clamped to the same, so a row
 * never claims to have been written "in 2 minutes".
 */
export const formatRelativeTime = (value: Date | string, now: Date = new Date()): string => {
  const then = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(then.getTime())) return '';
  const elapsed = now.getTime() - then.getTime();
  if (elapsed < MINUTE) return 'just now';

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  for (const { limit, unit, ms } of THRESHOLDS) {
    if (elapsed < limit) {
      return formatter.format(-Math.floor(elapsed / ms), unit);
    }
  }
  return formatter.format(-Math.floor(elapsed / YEAR), 'year');
};
